import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath} from 'reactflow';

import {GetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {MakeFieldsRequired} from 'util/Types';

import {AnalysisGraphEdgeData} from './analysis-graph/AnalysisGraphTypes';
import {
  ANALYSIS_FLOWCHART_BACKGROUND,
  ANALYSIS_FLOWCHART_EDGE_LABEL_HEIGHT,
  ANALYSIS_FLOWCHART_EDGE_LABEL_WIDTH,
} from './AnalysisFlowchartStyleConstants';
import DataFramePopover from './popovers/DataFramePopover';

/** @package */
export function EdgeLabelContents({
  dataFrame,
  showName,
}: {
  dataFrame: GetAnalysisDataFrame;
  showName: boolean;
}): JSX.Element {
  return (
    <DataFramePopover dataFrameId={dataFrame.id}>
      <div className={css(styles.label)}>
        <span className={css(styles.icon)}>
          <BenchlingIcon icon="table" />
        </span>
        {showName && <span>{dataFrame.name}</span>}
      </div>
    </DataFramePopover>
  );
}

export default function AnalysisFlowchartEdge(
  props: MakeFieldsRequired<EdgeProps<AnalysisGraphEdgeData>, 'data'>
): JSX.Element {
  const [edgePath, labelX, labelY] = getBezierPath(props);

  const {dataFrames, originatesDataFrameName} = props.data;

  return (
    <>
      <BaseEdge path={edgePath} />
      {dataFrames.length === 1 && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            className="nopan"
          >
            <EdgeLabelContents dataFrame={dataFrames[0]} showName={originatesDataFrameName} />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  icon: {
    color: Colors.GRAY_DARKER,
    height: ANALYSIS_FLOWCHART_EDGE_LABEL_HEIGHT,
    padding: 5,
  },
  label: {
    ...FONT_PROXIMA_12,
    // Ensure the line of the edge doesn't cut through the text
    backgroundColor: ANALYSIS_FLOWCHART_BACKGROUND,
    // Use set dimensions, so the label's size is straightforward to incorporate into the layout algorithm
    height: ANALYSIS_FLOWCHART_EDGE_LABEL_HEIGHT,
    maxWidth: ANALYSIS_FLOWCHART_EDGE_LABEL_WIDTH,
    // Ensure overflow text ellipsizes
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // Without these, the cursor will be 'grab' and not active the hover content
    cursor: 'pointer',
    pointerEvents: 'all',
  },
});
